import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "tools/routerCompat";
import { MSplitter, Button } from "components/Shared";
import { PdfDisplayControl, PdfDisplayDefinition, } from "interfaces/Phoenix.FormsDesigner.Models";
import { getPdfDefinition, updatePdfDefinition, importPdfChanges } from "requests/phoenixPdf";
import LoadingCover from "components/Shared/LoadingCover";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import uuid from "uuid/v4";
import { ensureAceLoaded } from "components/PhoenixFormsV4/PhoenixFormsDesigner/Designer/CodeEditor/CodeEditor";
import { connect } from "react-redux";
import { setCoreLayoutHeaderVisibility } from "actions/layout";
import Select from "react-select";
import PhoenixPdfDesignerPreview from "./PhoenixPdfDesignerPreview";
import PhoenixPdfDesignerProps from "./PhoenixPdfDesignerProps";
import MoveSectionDialog, { PdfMoveControlReference } from "./MoveSectionDialog";
import ImportChangesDialog, { pdfImportChanges } from "./ImportChangesDialog";
import useUnsavedChangesBlocker from "hooks/useUnsavedChangesBlocker";

interface Props {
  match: { params: { phoenixPdfId: string } },
  setHeaderVisibility: (visible: boolean) => void,

  // router
  history: any,
};

interface LabelEditDetails {
  control?: PdfDisplayControl;
  modifierLabelId?: string; // on adds, if specified this is the question id we are adding below. On remove, this is the question id we are deleting
};

interface PDFDesignerEditFunctions {
  addNewLabel: (details: LabelEditDetails) => void;
  deleteLabel: (questionId: string) => void;
  addNewSection: (belowSectionIndex: number, newSectionTitle?: string) => void;
  deleteChildren: (controlIndex: number) => void;
  renameChildren: (controlIndex: number, sectionTitle: string) => void;
  reorderChildren: (control: PdfDisplayControl, newOrder: string[]) => void;
  savePDFDef: () => void;
  moveToSection: (details: PdfMoveControlReference) => void;
};


export interface PDFDesignerProviderValue {
  phoenixPdfId: string;
  pdfDefinition: PdfDisplayDefinition;
  getLabelById: (id: string) => PdfDisplayControl;
  setSelectedLabel: (label: PdfDisplayControl) => void;
  selectedLabel: PdfDisplayControl;
  refreshPdfDef: (lastChangeType: PDFsDesignerLastChangeType) => void;
  pdfDefinitionUpdatedAt: number;
  editFns: PDFDesignerEditFunctions;
  lastChangeType: PDFsDesignerLastChangeType,
  lastChangeData: any,
  isSavingPdf: boolean,
  isShowingHidden: boolean
};

export const PhoenixPDFsDesignerContext = React.createContext<PDFDesignerProviderValue>(null);

export type PDFsDesignerLastChangeType =
  "initial load" |
  "add label" |
  "delete label" |
  "reorder label" |
  "move label" |
  "add section" |
  "delete children" |
  "rename children" |
  "reorder children" |
  "import changes" |
  "pdf edited" |
  "pdf saved";

const PhoenixPDFDesigner = ({ match: { params: { phoenixPdfId } }, setHeaderVisibility, history }: Props) => {
  const mounted = useRef(true);
  const _pdfDefRef = useRef<PdfDisplayDefinition>();
  const pdfDefinition = _pdfDefRef.current;
  const [pdfDefinitionUpdatedAt, setPdfDefinitionUpdatedAt] = useState<number>();
  const [lastChangeType, setLastChangeType] = useState<PDFsDesignerLastChangeType>(null);
  const [lastChangeData, setLastChangeData] = useState<any>();
  const [isSavingPdf, setIsSavingPdf] = useState<boolean>(false);
  const [isShowingHidden, setIsShowingHidden] = useState<boolean>(false);
  const [collapsedButtons, setCollapsedButtons] = useState<boolean>();
  const headerRef = useRef<HTMLDivElement>();
  const headerResize = useRef<ResizeObserver>();
  const [selectedLabel, setSelectedLabel] = useState<PdfDisplayControl>();
  const dialog = useContext<DialogProviderValue>(DialogContext);

  const refreshPdfDef = (lastChangeType: PDFsDesignerLastChangeType, lastChangeData: any = null) => {
    setLastChangeType(lastChangeType);
    setLastChangeData(lastChangeData);
    setPdfDefinitionUpdatedAt(Date.now());
  }

  useEffect(() => {
    setHeaderVisibility(false);

    getPdfDefinition(phoenixPdfId, (pdfDef) => {
      _pdfDefRef.current = pdfDef;
      refreshPdfDef("initial load");
      if (selectedLabel) setSelectedLabel(pdfDef.displayControls.find(l => l.id === selectedLabel.id));
    }, null, mounted);

    ensureAceLoaded();

    return () => {
      mounted.current = false;
      setHeaderVisibility(true);
    };
  }, []);

  const pdfHasUnsavedChanges =
    lastChangeType !== "initial load" && lastChangeType !== "pdf saved" && !!lastChangeType;

  useUnsavedChangesBlocker(pdfHasUnsavedChanges);

  const addNewLabel = (details: LabelEditDetails) => {
    let insertIndex: number;

    if (details.modifierLabelId) {
      insertIndex = details.control.children.findIndex(c => c.id === details.modifierLabelId) + 1;
    }
    else {
      insertIndex = details.control.children.length;
    }

    let newLabel = {
      id: uuid(),
      linkedControlId: null,
      dataFieldId: null,
      name: null,
      value: null,
      type: "Label",
      children: null,
      properties: {
        hide: false,
        hideNullOrEmpty: false,
        invertBoolean: false,
        parseAsBoolean: false,
      },
    } as PdfDisplayControl;


    details.control.children.splice(insertIndex, 0, newLabel);

    refreshPdfDef("add label");
  };

  const deleteLabel = (labelId: string, areYouSure: boolean = false) => {
    const label = getLabelById(labelId);
    if (!label?.control) return;

    if (!areYouSure) {
      dialog.showYesNo("Delete Label", `Are you sure you want to delete label "${label.control.name}"?`, () => deleteLabel(labelId, true));
    } else {
      const deleteIndex = label.parent.children.findIndex(c => c.id === labelId);
      label.parent.children.splice(deleteIndex, 1);

      refreshPdfDef("delete label");
    }
  };

  const addNewSection = (controlIndex: number, newSectionTitle?: string) => {
    if (!newSectionTitle) {
      dialog.prompt({
        title: "New Section",
        message: "Enter a name for the new section",
        okLabel: "Add Section",
        onOkClicked: name => addNewSection(controlIndex, name),
      });
    } else {
      let sections = pdfDefinition.displayControls;
      let newSection = {
        id: uuid(),
        linkedControlId: null,
        dataFieldId: null,
        name: newSectionTitle,
        value: null,
        type: "SubSectionGroup-Full",
        children: [],
        properties: {
          hide: false,
          hideNullOrEmpty: false,
          invertBoolean: false,
          parseAsBoolean: false,
        },
      } as PdfDisplayControl;

      sections.splice(controlIndex + 1, 0, newSection);
      refreshPdfDef("add section", controlIndex);
    }
  };

  const deleteChildren = (controlIndex: number, areYouSure: boolean = false) => {
    if (!areYouSure) {
      dialog.showYesNo("Delete Element", `Are you sure you want to delete ${pdfDefinition.displayControls[controlIndex].name} ? This will also delete all labels within the section.`, () => deleteChildren(controlIndex, true));
    } else {
      let sections = pdfDefinition.displayControls;
      let deleted = sections.splice(controlIndex, 1)[0];
      // delete all the labels from the section too
      pdfDefinition.displayControls = pdfDefinition.displayControls.filter(c => deleted.children.indexOf(c) === -1);
      refreshPdfDef("delete children");
    }
  };

  const renameChildren = (controlIndex: number, sectionTitle: string) => {
    pdfDefinition.displayControls[controlIndex].name = sectionTitle;
    refreshPdfDef("rename children");
  }

  const reorderChildren = (control: PdfDisplayControl, newElementIdOrder: string[]) => {
    if (control) {
      control.children = newElementIdOrder.map(id => control.children.find(e => e.id === id))
    } else {
      pdfDefinition.displayControls = newElementIdOrder.map(id => pdfDefinition.displayControls.find(e => e.id === id))
    }

    refreshPdfDef("reorder children");
  };

  const getLabelById = (id: string, parent: PdfDisplayControl = null) => {
    for (let control of (parent?.children ?? pdfDefinition.displayControls)) {
      if (control.id === id) {
        return ({ control, parent });
      }

      if (control.children?.length > 0) {
        let found = getLabelById(id, control);
        if (found) return found;
      }
    }

    return null;
  }

  const savePDFDef = (successCallback: () => void = null, errorCallback: () => void = null) => {
    setIsSavingPdf(true);
    updatePdfDefinition(phoenixPdfId, pdfDefinition, () => {
      setIsSavingPdf(false);
      refreshPdfDef("pdf saved");
      successCallback?.();
    }, errorCallback, mounted);
  };

  const showHide = () => {
    setIsShowingHidden(!isShowingHidden);
  };

  const importChanges = () => {
    importPdfChanges(phoenixPdfId, (changes) => {
        let pdfImportChanges = { removed: changes.removed, repeatChildrenAdded: changes.repeatChildrenAdded, rootControlsAdded: changes.rootControlsAdded };

        dialog.showYesNo(
          "Import Changes",
          <ImportChangesDialog pdfImportChanges={pdfImportChanges} pdfDefinition={pdfDefinition} />,
          () => applyImportChanges(pdfImportChanges),
          dialog.hide,
          "Apply",
          "Cancel",
          { className: "import-changes-dialog" }
        );
      }, null, mounted);
  };

  const applyImportChanges = (pdfImportChanges) => {
    console.log(pdfImportChanges)
    pdfImportChanges.rootControlsAdded.forEach((item, index) => {
      console.log(index)
      if (!item.control) return;
      const section = pdfDefinition.displayControls.find((c) => c.id === item.parentId);

      if (section) section.children.push(item.control);

    });

    pdfImportChanges.repeatChildrenAdded.forEach((item) => {
      const sections = pdfDefinition.displayControls;
      sections.forEach((section) => {
        const matchedRepeat = section.children.find((c) => c.id === item.parentId);
        if (matchedRepeat && item.control) {
          matchedRepeat.children.push(item.control);
        }
      });
    });

    pdfImportChanges.removed.forEach((rem) => {
      const sections = pdfDefinition.displayControls;
      for (const sec of sections) {
        let idx = sec.children.map((c) => c.id).indexOf(rem.id);
        if (idx >= 0) {
          sec.children.splice(idx, 1);
          return;
        }
        const repeats = sec.children.filter((c) => c.children && c.children.length);
        for (const rep of repeats) {
          idx = rep.children.map((c) => c.id).indexOf(rem.id);
          if (idx >= 0) {
            rep.children.splice(idx, 1);
            return;
          }
        }
      }
    });

    savePDFDef()
    refreshPdfDef("import changes")
  };

  const onBackToFormsClicked = () => {
    history.push("/phoenixforms-designer/forms-list");
  };

  const moveToSection = (details: PdfMoveControlReference) => {
    let moveControlReference = { destinationSectionId: null as string, controlId: details.controlId, sourceSection: details.sourceSection };

    if (!details.destinationSectionId) {
      dialog.showYesNo(
        "Move Label to new section",
        <MoveSectionDialog moveControlReference={moveControlReference} pdfDefinition={pdfDefinition} />,
        () => moveToSection(moveControlReference),
        dialog.hide,
        "Move Label",
        "Cancel",
        { className: "new-question-dialog", useFlexInsteadOfTransformForCentering: true }
      );
    } else {
      const labelToMove = getLabelById(details.controlId).control;
      const labelIndex = details.sourceSection.children.indexOf(labelToMove);
      const destinationArray = getLabelById(details.destinationSectionId).control;
      details.sourceSection.children.splice(labelIndex, 1);
      destinationArray.children.splice(destinationArray.children.length, 0, labelToMove);
      refreshPdfDef("move label")
    }
  }

  const value: PDFDesignerProviderValue = {
    phoenixPdfId,
    pdfDefinition,
    getLabelById,
    setSelectedLabel,
    selectedLabel,
    refreshPdfDef,
    pdfDefinitionUpdatedAt,
    lastChangeType,
    lastChangeData,
    isSavingPdf,
    isShowingHidden,
    editFns: {
      addNewLabel,
      deleteLabel,
      addNewSection,
      deleteChildren,
      renameChildren,
      reorderChildren,
      savePDFDef,
      moveToSection,
    },
  };

  let isLoaded = !!pdfDefinition;
  let loadingMessages: string[] = [];

  if (!isLoaded) loadingMessages.push("Loading PDF Data...");
  if (isSavingPdf) loadingMessages.push("Saving PDF Data...");

  let isBlocked = !!loadingMessages.length;

  let buttonDefs = [
    { onClick: showHide, label: isShowingHidden ? "Hide Hidden" : "Show Hidden" },
    { onClick: savePDFDef, label: "Save PDF Changes" },
    { onClick: importChanges, label: "Import Changes" },
    { onClick: onBackToFormsClicked, label: "Back To Forms List" },
  ];

  let buttons: ReactNode[];

  if (collapsedButtons) {
    buttons = [<Select
      // className={ReactSelectClassName}
      placeholder="PDF Actions"
      classNamePrefix="designer-header"
      isSearchable={false}
      options={buttonDefs.map(({ label, onClick }) => ({
        label,
        value: onClick,
      }))}
      onChange={selected => selected.value()}
    />];
  } else {
    buttons = buttonDefs.map(bd => <Button disabled={isBlocked} onClick={bd.onClick}>{bd.label}</Button>);
  }

  const titleResizeObserver = () => {
    if (!headerRef.current) return;
    setCollapsedButtons(headerRef.current.offsetWidth < 1500);
  }

  const titleDivResizeListenerRef = (element: HTMLDivElement) => {
    headerRef.current = element;
    if (element) {
      headerResize.current = new ResizeObserver(titleResizeObserver);
      headerResize.current.observe(element);
    } else {
      headerResize.current?.disconnect();
    }
  };

  return (
    <PhoenixPDFsDesignerContext.Provider value={value}>
      <div className="phoenixforms-designer">
        <div className="app-header-single-div" ref={titleDivResizeListenerRef}>
          <div className="header-text">{pdfDefinition ? pdfDefinition?.name : "Loading PDF Designer..."}</div>
          {buttons}
        </div>
        {isLoaded &&
          <MSplitter
            firstChildInitialSize="33%"
            firstChild={<PhoenixPdfDesignerProps />}
            secondChild={<PhoenixPdfDesignerPreview />}
          />
        }
      </div>
      {!!loadingMessages.length && <LoadingCover loadingMessages={loadingMessages} />}
    </PhoenixPDFsDesignerContext.Provider>
  );
}

const mapDispatchToProps = dispatch => ({
  setHeaderVisibility: (visible: boolean) => dispatch(setCoreLayoutHeaderVisibility(visible)),
});

export default withRouter(connect(null, mapDispatchToProps)(PhoenixPDFDesigner));